import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
} from '../utils/webComponents';
import { useNavigation, useRoute } from '../utils/navigation';
import { useLocation } from 'react-router-dom';
import { 
  School, 
  Person, 
  Email, 
  Lock, 
  Visibility, 
  VisibilityOff
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';

const RegisterScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const location = useLocation();
  const { register, loading, error, clearError } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [referrerId, setReferrerId] = useState(null);

  // Extract referrer ID from route params or URL
  useEffect(() => {
    const params = route.params || {};
    const state = location.state || {};
    const referrer = params.referrerId || state.referrerId;
    
    if (referrer) {
      setReferrerId(referrer);
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      const ref = urlParams.get('ref');
      if (ref) {
        setReferrerId(ref);
      }
    }
  }, [route.params, location.state]);

  const handleRegister = async () => {
    if (!name.trim() || !email.trim() || !password.trim() || !confirmPassword.trim()) {
      return;
    }
    if (password !== confirmPassword) {
      return;
    }
    if (password.length < 6) {
      return;
    }
    try {
      await register(email.trim(), password, name.trim(), referrerId);
    } catch (error) {
      // Error handled by context
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleRegister();
    }
  };

  const isFormValid = name.trim() && email.trim() && password.trim() && confirmPassword.trim() && password === confirmPassword && password.length >= 6;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <View style={styles.content}>
        {/* Logo */}
        <View style={[styles.logoContainer, { backgroundColor: theme.primary }]}>
          <School sx={{ fontSize: 32, color: 'white' }} />
        </View>

        {/* Title */}
        <Text style={[styles.title, { color: theme.text }]}>{t('auth.register')}</Text>
        <Text style={[styles.subtitle, { color: theme.textSecondary }]}>
          {t('auth.signUpToGetStarted')}
        </Text>

        {/* Error Message */}
        {error && (
          <View style={[styles.errorContainer, { backgroundColor: theme.error + '20', borderColor: theme.error }]}>
            <Text style={[styles.errorText, { color: theme.error }]}>{error}</Text>
            <TouchableOpacity onPress={clearError} style={styles.closeError}>
              <Text style={[styles.closeErrorText, { color: theme.error }]}>×</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Form */}
        <View style={styles.form}>
          <View style={styles.inputWrapper}>
            <View style={[styles.inputContainer, { backgroundColor: theme.inputBackground, borderColor: theme.inputBorder }]}>
              <View style={styles.iconWrapper}>
                <Person sx={{ fontSize: 20, color: theme.textMuted }} />
              </View>
              <TextInput
                style={[styles.input, { color: theme.inputText }]}
                placeholder={t('auth.name')}
                value={name}
                onChangeText={setName}
                onKeyPress={handleKeyPress}
                autoCapitalize="words"
              />
            </View>
          </View>

          <View style={styles.inputWrapper}>
            <View style={[styles.inputContainer, { backgroundColor: theme.inputBackground, borderColor: theme.inputBorder }]}>
              <View style={styles.iconWrapper}>
                <Email sx={{ fontSize: 20, color: theme.textMuted }} />
              </View>
              <TextInput
                style={[styles.input, { color: theme.inputText }]}
                placeholder={t('auth.email')}
                value={email}
                onChangeText={setEmail}
                onKeyPress={handleKeyPress}
                type="email"
                autoCapitalize="none"
              />
            </View>
          </View>

          <View style={styles.inputWrapper}>
            <View style={[styles.inputContainer, { backgroundColor: theme.inputBackground, borderColor: theme.inputBorder }]}>
              <View style={styles.iconWrapper}>
                <Lock sx={{ fontSize: 20, color: theme.textMuted }} />
              </View>
              <TextInput
                style={[styles.input, { color: theme.inputText }]}
                placeholder={t('auth.password')}
                value={password}
                onChangeText={setPassword}
                onKeyPress={handleKeyPress}
                secureTextEntry={!showPassword}
              />
              <TouchableOpacity
                onPress={() => setShowPassword(!showPassword)}
                style={styles.eyeButton}
              >
                {showPassword ? (
                  <VisibilityOff sx={{ fontSize: 20, color: theme.textMuted }} />
                ) : (
                  <Visibility sx={{ fontSize: 20, color: theme.textMuted }} />
                )}
              </TouchableOpacity>
            </View>
            {password.length > 0 && password.length < 6 && (
              <Text style={[styles.hint, { color: theme.error }]}>
                {t('auth.weakPassword')}
              </Text>
            )}
          </View>

          <View style={styles.inputWrapper}>
            <View style={[styles.inputContainer, { backgroundColor: theme.inputBackground, borderColor: theme.inputBorder }]}>
              <View style={styles.iconWrapper}>
                <Lock sx={{ fontSize: 20, color: theme.textMuted }} />
              </View>
              <TextInput
                style={[styles.input, { color: theme.inputText }]}
                placeholder={t('auth.confirmPassword')}
                value={confirmPassword}
                onChangeText={setConfirmPassword}
                onKeyPress={handleKeyPress}
                secureTextEntry={!showConfirmPassword}
              />
              <TouchableOpacity
                onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                style={styles.eyeButton}
              >
                {showConfirmPassword ? (
                  <VisibilityOff sx={{ fontSize: 20, color: theme.textMuted }} />
                ) : (
                  <Visibility sx={{ fontSize: 20, color: theme.textMuted }} />
                )}
              </TouchableOpacity>
            </View>
            {confirmPassword.length > 0 && password !== confirmPassword && (
              <Text style={[styles.hint, { color: theme.error }]}>
                {t('auth.passwordsDoNotMatch')}
              </Text>
            )}
          </View>

          <TouchableOpacity
            style={[
              styles.submitButton,
              { 
                backgroundColor: isFormValid ? theme.primary : theme.textMuted,
                opacity: loading ? 0.7 : 1
              }
            ]}
            onPress={handleRegister}
            disabled={loading || !isFormValid}
          >
            <Text style={[styles.submitButtonText, { color: theme.buttonText }]}>
              {loading ? t('common.loading') : t('auth.signUp')}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Sign In Link */}
        <View style={styles.linkContainer}>
          <Text style={[styles.linkText, { color: theme.textSecondary }]}>
            {t('auth.alreadyHaveAccount')}
          </Text>
          <TouchableOpacity onPress={() => navigation.navigate('Login')}>
            <Text style={[styles.link, { color: theme.primary }]}>{t('auth.signIn')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  content: {
    width: '100%',
    maxWidth: 400,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logoContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 32,
    textAlign: 'center',
  },
  errorContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 8,
    marginBottom: 24,
    borderWidth: 1,
  },
  errorText: {
    flex: 1,
    fontSize: 14,
  },
  closeError: {
    padding: 4,
    marginLeft: 8,
  },
  closeErrorText: {
    fontSize: 20,
    fontWeight: 'bold',
    lineHeight: 20,
  },
  form: {
    width: '100%',
    marginBottom: 24,
  },
  inputWrapper: {
    marginBottom: 16,
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 20,
    paddingVertical: 18,
    minHeight: 56,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
    marginLeft: 8,
  },
  input: {
    flex: 1,
    fontSize: 18,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    padding: 0,
  },
  eyeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
    marginLeft: 8,
    cursor: 'pointer',
  },
  hint: {
    fontSize: 12,
    marginTop: 6,
    marginLeft: 4,
  },
  submitButton: {
    width: '100%',
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  linkContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  linkText: {
    fontSize: 14,
  },
  link: {
    fontSize: 14,
    fontWeight: '600',
    cursor: 'pointer',
    marginLeft: 8,
  },
});

export default RegisterScreen;
